import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ClockIcon, MapPinIcon, NavigationIcon, CheckIcon } from "lucide-react";
import { outlets } from "../data/outlets";
import { isOutletOpen } from "../utils/outletStatus";
interface OutletsSectionProps {
  onReserve: (outletId: string) => void;
}
export function OutletsSection({
  onReserve
}: OutletsSectionProps) {
  const [selectedId, setSelectedId] = useState(outlets[0].id);
  const selected = outlets.find(o => o.id === selectedId) ?? outlets[0];
  const selectedOpen = isOutletOpen(selected);
  return <section id="locations" className="py-20 lg:py-28" data-id="element-236" data-mp="d41901p63p1t">
      <div className="mx-auto max-w-7xl px-5 sm:px-8" data-id="element-237" data-mp="d2gda8r4a02d">
        <div className="max-w-xl" data-id="element-238" data-mp="i3vvlt1wtsh2">
          <h2 className="text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-id="element-239" data-mp="i2ulprwmrc0x">
            Temukan sudut favoritmu
          </h2>
          <p className="mt-4 text-base leading-relaxed text-ink-muted" data-id="element-240" data-mp="2juqwgzaz4hun">
            Empat outlet, masing-masing dengan suasananya sendiri. Pilih salah satu untuk melihat
            keistimewaannya.
          </p>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]" data-id="element-241" data-mp="3ufyzafmv8c8t">
          <ul className="flex flex-col gap-2" aria-label="Daftar outlet" data-id="element-242" data-mp="3qewtth2cv3ye">
            {outlets.map(outlet => {
            const active = outlet.id === selectedId;
            const open = isOutletOpen(outlet);
            return <li key={outlet.id} data-id="element-243" data-mp="m0f8dn9o35wx">
                  <button type="button" onClick={() => setSelectedId(outlet.id)} aria-pressed={active} className={`flex w-full items-center justify-between gap-4 rounded-card px-5 py-4 text-left ring-1 transition-[background-color,box-shadow] duration-150 ${active ? "bg-white ring-sage" : "bg-transparent ring-cream-line hover:bg-white/60"}`} data-id="element-244" data-mp="hzwg5s3v3xu7">
                    <div data-id="element-245" data-mp="2ua4zbo5pk5h9">
                      <p className="text-base font-semibold text-ink" data-id="element-246" data-mp="2u6qv0adhhm76">{outlet.name}</p>
                      <p className="mt-0.5 text-sm text-ink-muted" data-id="element-247" data-mp="2z7en6zdg892z">{outlet.vibe}</p>
                    </div>
                    <span className={`flex items-center gap-1.5 whitespace-nowrap text-xs font-medium ${open ? "text-sage" : "text-ink-muted"}`} data-id="element-248" data-mp="2l7vlopf57vzn">
                      <span className={`h-1.5 w-1.5 rounded-full ${open ? "bg-sage" : "bg-ink-muted/50"}`} aria-hidden="true" data-id="element-249" data-mp="2g8skxh674bxh" />
                      {open ? "Buka" : "Tutup"}
                    </span>
                  </button>
                </li>;
          })}
          </ul>

          <div className="overflow-hidden rounded-card bg-espresso text-cream" data-id="element-250" data-mp="1slkprs2hej96">
            <AnimatePresence mode="wait" data-id="element-251" data-mp="1xi1dwm9okxm9">
              <motion.div key={selected.id} initial={{
              opacity: 0,
              x: 8
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -8
            }} transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} className="flex h-full flex-col" data-id="element-252" data-mp="1dwsuepkebiq5">
                <div className="aspect-[16/9] w-full overflow-hidden" data-id="element-253" data-mp="1iuu96yasgvvt">
                  <img src={selected.image} alt={`Outlet Kopi Lima Detik ${selected.name}`} className="h-full w-full object-cover" data-id="element-254" data-mp="1wvdcxt881fs6" />
                </div>
                <div className="flex flex-1 flex-col p-7 sm:p-10" data-id="element-255" data-mp="26k53bwxmvs0z">
                  <span className="self-start rounded-full bg-sage px-3 py-1 text-xs font-semibold" data-id="element-256" data-mp="21ltt3l3fffi8">
                    {selected.vibe}
                  </span>
                  <h3 className="mt-5 text-3xl font-semibold tracking-tight" data-id="element-257" data-mp="2fk1xuf6pfl3y">
                    Kopi Lima Detik {selected.name}
                  </h3>
                  <p className="mt-3 max-w-lg text-base leading-relaxed text-cream/75" data-id="element-258" data-mp="8ynr0l9ah81a">
                    {selected.description}
                  </p>

                  <dl className="mt-8 grid gap-5 sm:grid-cols-2" data-id="element-259" data-mp="dum9js7xrvts">
                    <div className="flex gap-3" data-id="element-260" data-mp="io7btwgkbbys">
                      <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-sage-light" aria-hidden="true" data-id="element-261" data-mp="nmim28arrohj" />
                      <div data-id="element-262" data-mp="3m4nxb39vca26">
                        <dt className="sr-only" data-id="element-263" data-mp="3m5fom1wfnlmn">Alamat</dt>
                        <dd className="text-sm leading-relaxed text-cream/85" data-id="element-264" data-mp="3r3h64ie7ncw0">
                          {selected.address}, {selected.area}
                        </dd>
                      </div>
                    </div>
                    <div className="flex gap-3" data-id="element-265" data-mp="3z1e6cxdhtt0">
                      <ClockIcon className="mt-0.5 h-5 w-5 shrink-0 text-sage-light" aria-hidden="true" data-id="element-266" data-mp="3wom36xwi328" />
                      <div data-id="element-267" data-mp="1bepckqttd191">
                        <dt className="sr-only" data-id="element-268" data-mp="1fdeyx8wtxt4t">Jam operasional</dt>
                        <dd className="text-sm leading-relaxed text-cream/85" data-id="element-269" data-mp="1fg126wcotmh8">
                          {selected.hoursLabel}
                          <span className={`block font-medium ${selectedOpen ? "text-sage-light" : "text-cream/50"}`} data-id="element-270" data-mp="2vuncm2xteaw3">
                            {selectedOpen ? "Sedang buka" : "Sedang tutup"}
                          </span>
                        </dd>
                      </div>
                    </div>
                  </dl>

                  <ul className="mt-8 grid gap-2.5 border-t border-espresso-line pt-6 sm:grid-cols-2" data-id="element-271" data-mp="2c6ee7b5oh8o5">
                    {selected.features.map(f => <li key={f} className="flex items-center gap-2 text-sm text-cream/85" data-id="element-272" data-mp="285clld69nuv5">
                        <CheckIcon className="h-4 w-4 text-sage-light" aria-hidden="true" data-id="element-273" data-mp="23719swwevaaa" />
                        {f}
                      </li>)}
                  </ul>

                  <div className="mt-auto flex flex-col gap-3 pt-10 sm:flex-row" data-id="element-274" data-mp="1fo9kl5e1qkk">
                    <button type="button" onClick={() => onReserve(selected.id)} className="rounded-full bg-cream px-6 py-3 text-sm font-semibold text-espresso transition-[background-color,transform] duration-150 ease-out hover:bg-white active:scale-[0.97]" data-id="element-275" data-mp="kiq1cqtu3og6">
                      Reservasi meja di sini
                    </button>
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`Kopi Lima Detik ${selected.address}`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full border border-cream/30 px-6 py-3 text-sm font-semibold text-cream transition-colors duration-150 hover:border-cream" data-id="element-276" data-mp="148a9dz23ksn5">
                      <NavigationIcon className="h-4 w-4" aria-hidden="true" data-id="element-277" data-mp="3jzenmbjmzsqv" />
                      Petunjuk arah
                    </a>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}